import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  HostListener,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
  Subject,
  distinctUntilChanged,
  takeUntil,
  map,
  tap,
  filter,
  merge,
  delay,
  Observable,
  startWith,
  catchError,
  EMPTY,
  debounceTime,
} from 'rxjs';
import {
  NotificationFilters,
  NotificationType,
  NotificationPriority,
  NotificationStatus,
  NotificationSortBy,
  SortOrder,
  Tag,
} from 'src/app/models/notification.model';
import { NotificationService } from 'src/app/services/notification.service';
import { NotificationCategoriesService } from 'src/app/shared/notification-categories/notification-categories.service';

interface FilterOption<T = number> {
  value: T;
  label: string;
}

interface FilterFormValue {
  category: number | null;
  type: NotificationType | null;
  priority: NotificationPriority | null;
  status: NotificationStatus | null;
  dateFrom: Date | null;
  dateTo: Date | null;
  read: boolean | null;
  tags: string[];
  searchText: string;
  sortBy: NotificationSortBy;
  sortOrder: SortOrder;
}

@Component({
  selector: 'app-notification-filter-bar',
  templateUrl: './notification-filter-bar.component.html',
  styleUrls: ['./notification-filter-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationFilterBarComponent implements OnInit, OnDestroy {
  @Output() filtersChanged = new EventEmitter<NotificationFilters>();
  @ViewChild('tagInput') tagInput!: ElementRef<HTMLInputElement>;

  readonly filterForm: FormGroup;
  activeFiltersCount = 0;
  advancedFiltersCount = 0;
  showAdvancedFilters = false;
  currentSortValue = `${NotificationSortBy.CreatedAt}:${SortOrder.Desc}`;
  isUnreadChecked = false;

  availableTags: Tag[] = [];
  popularTags: Tag[] = [];
  selectedTags: Tag[] = [];
  filteredTags!: Observable<Tag[]>;
  tagInputControl = new FormControl('');
  showTagDropdown = false;

  categoryOptions: FilterOption<number>[] = [];

  readonly typeOptions: FilterOption<NotificationType>[] = [
    { value: NotificationType.INFO, label: 'notifications.types.info' },
    { value: NotificationType.WARNING, label: 'notifications.types.warning' },
    { value: NotificationType.ALERT, label: 'notifications.types.alert' },
    { value: NotificationType.SUCCESS, label: 'notifications.types.success' },
  ];

  readonly priorityOptions: FilterOption<NotificationPriority>[] = [
    { value: NotificationPriority.LOW, label: 'notifications.priorities.low' },
    { value: NotificationPriority.MEDIUM, label: 'notifications.priorities.medium' },
    { value: NotificationPriority.HIGH, label: 'notifications.priorities.high' },
    { value: NotificationPriority.URGENT, label: 'notifications.priorities.urgent' },
  ];

  readonly statusOptions: FilterOption<NotificationStatus>[] = [
    { value: NotificationStatus.PENDING, label: 'notifications.statuses.pending' },
    { value: NotificationStatus.SENT, label: 'notifications.statuses.sent' },
    { value: NotificationStatus.DELIVERED, label: 'notifications.statuses.delivered' },
    { value: NotificationStatus.READ, label: 'notifications.statuses.read' },
    { value: NotificationStatus.FAILED, label: 'notifications.statuses.failed' },
    { value: NotificationStatus.EXPIRED, label: 'notifications.statuses.expired' },
    { value: NotificationStatus.ARCHIVED, label: 'notifications.statuses.archived' },
  ];

  private readonly destroy$ = new Subject<void>();
  private readonly applyFilters$ = new Subject<void>();
  private isUpdatingUrl = false;
  private pendingTagNames: string[] = [];

  constructor(
    private readonly fb: FormBuilder,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly notificationService: NotificationService,
    private readonly categoriesService: NotificationCategoriesService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.filterForm = this.createForm();
  }

  ngOnInit(): void {
    this.setupUrlSync();
    this.setupFilterListeners();
    this.setupFilterApplication();
    this.loadTags();
    this.loadCategories();
    this.setupTagsFilter();
  }

  private loadCategories(): void {
    this.categoriesService
      .list()
      .pipe(
        takeUntil(this.destroy$),
        tap((categories) => {
          this.categoryOptions = categories.map((c) => ({
            value: c.id,
            label: this.categoriesService.resolveLabel(c),
          }));
          this.cdr.markForCheck();
        }),
        catchError((error: unknown) => {
          console.error('Error loading notification categories:', error);
          return EMPTY;
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private createForm(): FormGroup {
    return this.fb.group({
      category: [null],
      type: [null],
      priority: [null],
      status: [null],
      dateFrom: [null],
      dateTo: [null],
      read: [null],
      tags: [[] as string[]],
      searchText: [''],
      sortBy: [NotificationSortBy.CreatedAt],
      sortOrder: [SortOrder.Desc],
    });
  }

  private loadTags(): void {
    this.notificationService
      .getTags()
      .pipe(
        takeUntil(this.destroy$),
        tap((data) => {
          this.availableTags = data.tags;
          this.popularTags = data.popularTags;
          if (this.pendingTagNames.length > 0) {
            this.selectedTags = this.availableTags.filter((tag) =>
              this.pendingTagNames.includes(tag.name)
            );
            this.pendingTagNames = [];
          }
          this.cdr.markForCheck();
        }),
        catchError((error: unknown) => {
          console.error('Error loading tags:', error);
          return EMPTY;
        })
      )
      .subscribe();
  }

  private setupTagsFilter(): void {
    this.filteredTags = this.tagInputControl.valueChanges.pipe(
      startWith(''),
      map((value) => this.filterAvailableTags(value || ''))
    );
  }

  private filterAvailableTags(filterValue: string | Tag | null): Tag[] {
    const searchText = typeof filterValue === 'string' ? filterValue : '';
    const filterLower = searchText.toLowerCase();
    return this.availableTags.filter((tag) => tag.name.toLowerCase().includes(filterLower));
  }

  private setupUrlSync(): void {
    this.route.queryParams
      .pipe(
        takeUntil(this.destroy$),
        filter(() => !this.isUpdatingUrl),
        map((params) => this.parseUrlParams(params)),
        tap((filters) => {
          this.filterForm.patchValue(filters, { emitEvent: false });
          this.updateDerivedState();
          if (this.advancedFiltersCount > 0) {
            this.showAdvancedFilters = true;
          }
          this.emitFiltersFromForm();
        })
      )
      .subscribe();
  }

  private parseUrlParams(params: Params): Partial<FilterFormValue> {
    const filters: Partial<FilterFormValue> = {};

    if (params['category']) {
      filters.category = Number(params['category']);
    }
    if (params['type']) {
      filters.type = Number(params['type']);
    }
    if (params['priority']) {
      filters.priority = Number(params['priority']);
    }
    if (params['status']) {
      filters.status = Number(params['status']);
    }
    if (params['dateFrom']) {
      filters.dateFrom = new Date(params['dateFrom']);
    }
    if (params['dateTo']) {
      filters.dateTo = new Date(params['dateTo']);
    }
    if (params['read'] !== undefined) {
      filters.read = params['read'] === 'true';
    }
    if (params['tags']) {
      const tagNames = params['tags'].split(',').filter((t: string) => t.trim());
      filters.tags = tagNames;
      this.pendingTagNames = tagNames;
    }
    if (params['searchText']) {
      filters.searchText = params['searchText'];
    }
    if (params['sortBy']) {
      filters.sortBy = Number(params['sortBy']);
    }
    if (params['sortOrder']) {
      filters.sortOrder = params['sortOrder'];
    }
    return filters;
  }

  private setupFilterListeners(): void {
    const searchText$ = this.filterForm
      .get('searchText')!
      .valueChanges.pipe(debounceTime(500), distinctUntilChanged());

    const otherFilters$ = merge(
      ...[
        'category',
        'type',
        'priority',
        'status',
        'dateFrom',
        'dateTo',
        'read',
        'tags',
        'sortBy',
        'sortOrder',
      ].map((field) => this.filterForm.get(field)!.valueChanges.pipe(delay(0)))
    );

    merge(searchText$, otherFilters$)
      .pipe(
        takeUntil(this.destroy$),
        tap(() => this.applyFilters$.next())
      )
      .subscribe();
  }

  private setupFilterApplication(): void {
    this.applyFilters$
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          this.updateDerivedState();
          this.updateUrl();
          this.emitFiltersFromForm();
        })
      )
      .subscribe();
  }

  private updateDerivedState(): void {
    const formValue = this.filterForm.value as FilterFormValue;

    this.calculateActiveFilters(formValue);
    this.updateSortValue(formValue);
    this.updateUnreadChecked(formValue);
  }

  private calculateActiveFilters(formValue: FilterFormValue): void {
    let totalCount = 0;
    let advancedCount = 0;

    if (formValue.dateFrom) {
      totalCount++;
      advancedCount++;
    }
    if (formValue.dateTo) {
      totalCount++;
      advancedCount++;
    }
    if (formValue.sortBy && formValue.sortBy !== NotificationSortBy.CreatedAt) {
      totalCount++;
      advancedCount++;
    }
    if (formValue.sortOrder && formValue.sortOrder !== SortOrder.Desc) {
      totalCount++;
      advancedCount++;
    }
    if (formValue.category) {
      totalCount++;
      advancedCount++;
    }
    if (formValue.type) {
      totalCount++;
      advancedCount++;
    }
    if (formValue.priority) {
      totalCount++;
      advancedCount++;
    }
    if (formValue.status) {
      totalCount++;
      advancedCount++;
    }
    if (formValue.tags && formValue.tags.length > 0) {
      totalCount += formValue.tags.length;
      advancedCount += formValue.tags.length;
    }
    if (formValue.read !== null) {
      totalCount++;
    }
    if (formValue.searchText) {
      totalCount++;
    }

    this.activeFiltersCount = totalCount;
    this.advancedFiltersCount = advancedCount;
  }

  private updateSortValue(formValue: FilterFormValue): void {
    this.currentSortValue = `${formValue.sortBy}:${formValue.sortOrder}`;
  }

  private updateUnreadChecked(formValue: FilterFormValue): void {
    this.isUnreadChecked = formValue.read === false;
  }

  private emitFiltersFromForm(): void {
    const formValue = this.filterForm.value as FilterFormValue;
    const filters = this.buildFiltersFromFormValue(formValue);
    this.filtersChanged.emit(filters);
  }

  private buildFiltersFromFormValue(formValue: FilterFormValue): NotificationFilters {
    const filters: NotificationFilters = {};

    if (formValue.category) {
      filters.category = formValue.category;
    }
    if (formValue.type) {
      filters.type = formValue.type;
    }
    if (formValue.priority) {
      filters.priority = formValue.priority;
    }
    if (formValue.status) {
      filters.status = formValue.status;
    }
    if (formValue.dateFrom) {
      filters.dateFrom = formValue.dateFrom;
    }
    if (formValue.dateTo) {
      const dateTo = new Date(formValue.dateTo);
      dateTo.setHours(23, 59, 59, 999);
      filters.dateTo = dateTo;
    }
    if (formValue.read !== null && formValue.read !== undefined) {
      filters.read = formValue.read;
    }
    if (formValue.tags && formValue.tags.length > 0) {
      filters.tags = formValue.tags;
    }
    if (formValue.searchText) {
      filters.searchText = formValue.searchText;
    }
    if (formValue.sortBy) {
      filters.sortBy = formValue.sortBy;
    }
    if (formValue.sortOrder) {
      filters.sortOrder = formValue.sortOrder;
    }
    return filters;
  }

  private updateUrl(): void {
    const formValue = this.filterForm.value as FilterFormValue;
    const queryParams = this.buildQueryParams(formValue);

    this.isUpdatingUrl = true;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
    });

    setTimeout(() => {
      this.isUpdatingUrl = false;
    }, 100);
  }

  private buildQueryParams(formValue: FilterFormValue): Params {
    const queryParams: Params = {};

    queryParams['category'] = formValue.category || null;
    queryParams['type'] = formValue.type || null;
    queryParams['priority'] = formValue.priority || null;
    queryParams['status'] = formValue.status || null;
    queryParams['dateFrom'] = formValue.dateFrom
      ? (formValue.dateFrom instanceof Date ? formValue.dateFrom.toISOString() : formValue.dateFrom)
      : null;
    queryParams['dateTo'] = formValue.dateTo
      ? (formValue.dateTo instanceof Date ? formValue.dateTo.toISOString() : formValue.dateTo)
      : null;
    queryParams['read'] = formValue.read !== null ? formValue.read : null;
    queryParams['tags'] = formValue.tags && formValue.tags.length > 0
      ? formValue.tags.join(',')
      : null;
    queryParams['searchText'] = formValue.searchText || null;
    queryParams['sortBy'] = formValue.sortBy && formValue.sortBy !== NotificationSortBy.CreatedAt
      ? formValue.sortBy
      : null;
    queryParams['sortOrder'] = formValue.sortOrder && formValue.sortOrder !== SortOrder.Desc
      ? formValue.sortOrder
      : null;

    return queryParams;
  }

  onSortChange(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    const [sortByStr, sortOrderStr] = selectElement.value.split(':');
    const sortBy = Number(sortByStr) as NotificationSortBy;
    const sortOrder = sortOrderStr as SortOrder;
    this.setSorting(sortBy, sortOrder);
  }

  setSorting(sortBy: NotificationSortBy, sortOrder: SortOrder): void {
    this.filterForm.patchValue({ sortBy, sortOrder }, { emitEvent: false });
    this.applyFilters$.next();
  }

  toggleUnreadOnly(): void {
    const currentValue = this.filterForm.get('read')?.value;
    const newValue = currentValue === false ? null : false;
    this.filterForm.patchValue({ read: newValue }, { emitEvent: false });
    this.applyFilters$.next();
  }

  toggleAdvancedFilters(): void {
    this.showAdvancedFilters = !this.showAdvancedFilters;
  }

  clearFilters(): void {
    this.filterForm.reset(
      {
        category: null,
        type: null,
        priority: null,
        status: null,
        dateFrom: null,
        dateTo: null,
        read: null,
        tags: [],
        searchText: '',
        sortBy: NotificationSortBy.CreatedAt,
        sortOrder: SortOrder.Desc,
      },
      { emitEvent: false }
    );

    this.selectedTags = [];
    this.activeFiltersCount = 0;
    this.advancedFiltersCount = 0;
    this.currentSortValue = `${NotificationSortBy.CreatedAt}:${SortOrder.Desc}`;
    this.isUnreadChecked = false;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
    });

    this.emitFiltersFromForm();
  }

  clearSearchText(): void {
    this.filterForm.patchValue({ searchText: '' });
  }

  removeTag(tag: Tag): void {
    const index = this.selectedTags.findIndex((t) => t.id === tag.id);
    if (index >= 0) {
      this.selectedTags.splice(index, 1);
      this.filterForm.patchValue({ tags: this.getSelectedTagNames() });
      this.tagInputControl.setValue(this.tagInputControl.value);
    }
  }

  addTagFromButton(tag: Tag): void {
    const selectedTagIds = this.selectedTags.map((t) => t.id);
    if (!selectedTagIds.includes(tag.id)) {
      this.selectedTags.push(tag);
      this.filterForm.patchValue({ tags: this.getSelectedTagNames() });
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const tagsContainer = target.closest('.tags-filter-container');
    if (!tagsContainer && this.showTagDropdown) {
      this.showTagDropdown = false;
      this.cdr.markForCheck();
    }
  }

  selectTagFromDropdown(tag: Tag): void {
    const selectedTagIds = this.selectedTags.map((t) => t.id);
    if (!selectedTagIds.includes(tag.id)) {
      this.selectedTags.push(tag);
      this.filterForm.patchValue({ tags: this.getSelectedTagNames() });
    }
    this.tagInputControl.setValue('');
    this.showTagDropdown = false;
  }

  toggleTagSelection(tag: Tag): void {
    const index = this.selectedTags.findIndex((t) => t.id === tag.id);
    if (index >= 0) {
      this.selectedTags.splice(index, 1);
    } else {
      this.selectedTags.push(tag);
    }
    this.filterForm.patchValue({ tags: this.getSelectedTagNames() });
    this.tagInputControl.setValue('');
  }

  isTagSelected(tag: Tag): boolean {
    return this.selectedTags.some((t) => t.id === tag.id);
  }

  get sortValue(): string {
    const sortBy = this.filterForm.get('sortBy')?.value ?? NotificationSortBy.CreatedAt;
    const sortOrder = this.filterForm.get('sortOrder')?.value ?? SortOrder.Desc;
    return `${sortBy}:${sortOrder}`;
  }

  private getSelectedTagNames(): string[] {
    return this.selectedTags.map((t) => t.name);
  }
}
